/**
 * OpsTickClient — fire-and-forget POST for the verification station's 10s
 * keystroke tick. Bypasses the shared api-client 401-bounce because these
 * ticks are ambient telemetry, not user-intent actions — a session that
 * times out should not drag the operator to login mid-form.
 *
 * Buffers up to 3 failed payloads (network errors OR 5xx). Drops everything
 * beyond that. The next successful POST drains the buffer first, then sends
 * the fresh tick. 4xx responses are treated as permanent failures and
 * silently dropped (e.g. 204 when the tenant opts out).
 */
export interface OperatorTickPayload {
  documentId: string;
  step: string;
  keystrokeDelta: number;
  activeKeyingDeltaMs: number;
  kpmSample: number;
}

export class OpsTickClient {
  private buffer: OperatorTickPayload[] = [];
  private readonly endpoint: string;
  private readonly maxBuffer = 3;

  constructor(opts: { endpoint: string }) {
    this.endpoint = opts.endpoint;
  }

  /**
   * POST a tick. Drains the failure buffer in order before the fresh
   * payload. Any payload that fails (network error or 5xx) is re-queued,
   * subject to the 3-item ceiling.
   */
  async flush(tick: OperatorTickPayload): Promise<void> {
    const toSend = [...this.buffer, tick];
    this.buffer = [];
    for (const payload of toSend) {
      try {
        const res = await fetch(this.endpoint, {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(payload),
          credentials: "include",
        });
        if (!res.ok && res.status >= 500) {
          this.addToBuffer(payload);
        }
        // 4xx responses are silently dropped (permanent — no retry).
      } catch {
        // Network error — re-queue.
        this.addToBuffer(payload);
      }
    }
  }

  bufferSize(): number {
    return this.buffer.length;
  }

  private addToBuffer(p: OperatorTickPayload): void {
    if (this.buffer.length >= this.maxBuffer) return;
    this.buffer.push(p);
  }
}

export const opsTickClient = new OpsTickClient({
  endpoint: "/api/ops/operator-tick",
});
